import { useEffect, useMemo, useState } from "react";
import { Plus, Trash2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import type { UseFormReturn } from "react-hook-form";
import { Button, Checkbox, Input } from "@akuna/ui";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Autocomplete } from "@/components/ui/autocomplete";
import { MedicationAutocomplete, type MedicationWithRelations } from "../MedicationAutocomplete";
import { useMedications } from "@/hooks/useMedications";
import type {
  ManipulatedComponentInput,
  ManipulatedPayloadInput,
  MedicalPrescriptionInput,
} from "@/lib/validations/medical-prescription";
import { cn } from "@/lib/utils";
import { AddItemTrigger } from "./AddItemTrigger";
import { ExpandedItemShell } from "./ExpandedItemShell";
import { ItemCard } from "./ItemCard";
import type { ExpandedState } from "./PrescriptionEditor";

const EMPTY_PAYLOAD: ManipulatedPayloadInput = {
  components: [{ medication_id: "", name: "", concentration: "", free_text: "" }],
  pharmaceutical_form: "",
  treatment_days: "",
  total_doses: "",
  route: "",
  instructions: "",
  free_text: "",
};

const hasContent = (payload: ManipulatedPayloadInput | null | undefined) =>
  Boolean(
    payload &&
      (payload.free_text?.trim() ||
        payload.components?.some((c) => c.name?.trim() || c.free_text?.trim())),
  );

export function ManipulatedFormulaEditor({
  form,
  clinicId,
  mobile = false,
  expanded,
  onOpen,
  onClose,
  onDraftChange,
}: {
  form: UseFormReturn<MedicalPrescriptionInput>;
  clinicId: string;
  mobile?: boolean;
  expanded: ExpandedState | null;
  onOpen: () => void;
  onClose: () => void;
  onDraftChange: (payload: ManipulatedPayloadInput | null) => void;
}) {
  const { t } = useTranslation("prescriptions");
  const committed = form.watch("manipulated_payload") as ManipulatedPayloadInput | undefined;
  const regime = form.watch("control_regime");
  const formulaExists = hasContent(committed);
  const payloadError = (form.formState.errors as { manipulated_payload?: { components?: { message?: string }; message?: string } }).manipulated_payload;
  const errorMessage = payloadError?.components?.message ?? payloadError?.message;

  const summary = useMemo(() => {
    if (!committed) return { title: "", summary: "", aside: "" };
    const title = [committed.pharmaceutical_form?.trim() || t("editor.formula.title"), committed.total_doses ? t("editor.formula.doses", { count: Number(committed.total_doses) || 0 }) : ""].filter(Boolean).join(" · ");
    const lines = committed.free_text?.trim()
      ? committed.free_text.trim().split("\n").slice(0, 3).join("\n")
      : (committed.components ?? []).map((c) => [c.name, c.concentration].filter(Boolean).join(" — ") || c.free_text || "").filter(Boolean).join("\n");
    const aside = [committed.route, committed.treatment_days ? t("editor.formula.days", { count: Number(committed.treatment_days) || 0 }) : ""].filter(Boolean).join(" · ");
    return { title, summary: lines, aside };
  }, [committed, t]);

  return (
    <div className="flex flex-col gap-2.5">
      {expanded ? (
        <FormulaCard
          key="formula-edit"
          initial={formulaExists ? (committed as ManipulatedPayloadInput) : null}
          clinicId={clinicId}
          mobile={mobile}
          onCancel={onClose}
          onDraftChange={onDraftChange}
          onCommit={(payload) => {
            form.setValue("manipulated_payload", payload, { shouldDirty: true });
            form.clearErrors("manipulated_payload");
            onClose();
          }}
        />
      ) : formulaExists ? (
        <ItemCard
          index={0}
          title={summary.title}
          summary={summary.summary}
          aside={summary.aside}
          mobile={mobile}
          onEdit={onOpen}
          onRemove={() => form.setValue("manipulated_payload", EMPTY_PAYLOAD, { shouldDirty: true })}
        />
      ) : (
        <AddItemTrigger label={t("editor.formula.add")} mobile={mobile} onClick={onOpen} />
      )}
      {errorMessage ? <p className="text-sm text-destructive" role="alert">{errorMessage}</p> : null}

      <div className="rounded-xl bg-secondary/60 p-3 text-[12.5px]">
        <div className="flex items-start gap-2.5">
          <Checkbox
            id="mapa-controlled"
            checked={regime === "manipulated_controlled_mapa"}
            onCheckedChange={(checked) => {
              if (checked) {
                form.setValue("control_regime", "manipulated_controlled_mapa", { shouldValidate: true });
                form.setValue("validity_in_days", 30, { shouldValidate: true });
              } else {
                form.setValue("control_regime", "none", { shouldValidate: true });
              }
            }}
          />
          <div>
            <Label htmlFor="mapa-controlled" className="cursor-pointer text-sm font-medium">{t("manipulated.mapaControlledCheckbox")}</Label>
            <p className="text-muted-foreground">{t("manipulated.mapaControlledDescription")}</p>
          </div>
        </div>
      </div>
    </div>
  );
}

function FormulaCard({
  initial,
  clinicId,
  mobile,
  onCommit,
  onCancel,
  onDraftChange,
}: {
  initial: ManipulatedPayloadInput | null;
  clinicId: string;
  mobile: boolean;
  onCommit: (payload: ManipulatedPayloadInput) => void;
  onCancel: () => void;
  onDraftChange: (payload: ManipulatedPayloadInput | null) => void;
}) {
  const { t } = useTranslation("prescriptions");
  const [payload, setPayload] = useState<ManipulatedPayloadInput>(() => ({ ...EMPTY_PAYLOAD, ...(initial ?? {}), components: initial?.components?.length ? initial.components : EMPTY_PAYLOAD.components }));
  const [mode, setMode] = useState<"fields" | "free">(initial?.free_text?.trim() ? "free" : "fields");
  const kindsOf = (components: ManipulatedComponentInput[]) =>
    components.map((c) => (!c.name?.trim() && c.free_text?.trim() ? "free" : "component"));
  // Parallel to payload.components: free-text lines have no active ingredient/concentration columns.
  const [lineKinds, setLineKinds] = useState<Array<"component" | "free">>(() => kindsOf(initial?.components ?? EMPTY_PAYLOAD.components));
  const [search, setSearch] = useState("");
  const [error, setError] = useState<string | null>(null);
  const { data: medications = [], isLoading } = useMedications({ searchTerm: search, includeControlled: true });

  const patch = (changes: Partial<ManipulatedPayloadInput>) => setPayload((current) => ({ ...current, ...changes }));
  const setComponent = (index: number, changes: Partial<ManipulatedComponentInput>) =>
    setPayload((current) => ({ ...current, components: current.components.map((c, i) => (i === index ? { ...c, ...changes } : c)) }));

  useEffect(() => {
    onDraftChange(hasContent(payload) ? payload : null);
  }, [payload, onDraftChange]);
  useEffect(() => () => onDraftChange(null), [onDraftChange]);

  const dirty = JSON.stringify(payload) !== JSON.stringify(initial ?? EMPTY_PAYLOAD);

  const handleConfirm = () => {
    const cleaned: ManipulatedPayloadInput =
      mode === "free"
        ? { ...payload, components: [], pharmaceutical_form: "", free_text: payload.free_text ?? "" }
        : { ...payload, free_text: "", components: payload.components.filter((c) => c.name?.trim() || c.free_text?.trim()) };
    if (mode === "free" ? !cleaned.free_text?.trim() : cleaned.components.length === 0) {
      setError(mode === "free" ? t("editor.formula.freeRequired") : t("editor.formula.componentRequired"));
      return;
    }
    onCommit(cleaned);
  };

  const label = "text-sm font-medium";
  const inputCls = mobile ? "h-12 text-base" : undefined;
  const routes = ["oral", "subcutaneous", "intramuscular", "intravenous", "topical", "ophthalmic", "otic", "transdermal"].map((k) => t(`composer.optionSets.routes.${k}`));
  const forms = ["capsule", "oralPaste", "suspension", "cookie", "transdermalGel"].map((k) => t(`editor.formula.forms.${k}`));

  return (
    <ExpandedItemShell
      title={initial ? t("editor.formula.editTitle") : t("editor.formula.addTitle")}
      ordinal={1}
      mobile={mobile}
      dirty={dirty}
      onCancel={onCancel}
      onConfirm={handleConfirm}
      confirmLabel={initial ? t("editor.formula.saveFormula") : t("editor.formula.addFormula")}
      mode={{
        value: mode,
        onChange: setMode,
        labels: { fields: t("editor.mode.fields"), fieldsHint: t("editor.formula.fieldsHint"), free: t("editor.mode.free"), freeHint: t("editor.formula.freeHint") },
      }}
    >
      {mode === "fields" ? (
        <>
          <div className="space-y-2">
            <Label className={label}>{t("editor.formula.pharmaceuticalForm")}</Label>
            <Autocomplete options={forms.map((f) => ({ label: f, value: f }))} value={payload.pharmaceutical_form ?? ""} onChange={(v) => patch({ pharmaceutical_form: v })} placeholder={t("composer.selectOrType")} emptyMessage={t("composer.noSuggestions")} popoverPortalled={false} />
          </div>

          <div className="space-y-2">
            <p className={label}>{t("manipulated.formulaTitle")}</p>
            {payload.components.map((component, index) => {
              const freeLine = lineKinds[index] === "free";
              return (
                <div key={index} className={cn("grid items-center gap-1.5", freeLine ? "grid-cols-[1fr_44px]" : "grid-cols-[1.5fr_1fr_44px]")}>
                  {freeLine ? (
                    <Input aria-label={t("editor.formula.freeLine")} value={component.free_text ?? ""} onChange={(e) => setComponent(index, { free_text: e.target.value })} className={cn(inputCls, "border-dashed")} placeholder={t("editor.formula.freeLinePlaceholder")} />
                  ) : (
                    <>
                      <MedicationAutocomplete
                        popoverPortalled={false}
                        medications={medications as MedicationWithRelations[]}
                        isLoading={isLoading}
                        value={component.name ?? ""}
                        selectedMedicationId={component.medication_id ?? ""}
                        onChange={(value) => { setSearch(value); setComponent(index, { name: value, medication_id: "" }); }}
                        onSelectMedication={(medication, displayName) => { setSearch(""); setComponent(index, { name: displayName ?? medication.name, medication_id: medication.id }); }}
                        placeholder={t("manipulated.activeIngredientPlaceholder")}
                      />
                      <Input aria-label={t("manipulated.concentration")} value={component.concentration ?? ""} onChange={(e) => setComponent(index, { concentration: e.target.value })} className={inputCls} placeholder={t("manipulated.concentrationPlaceholder")} />
                    </>
                  )}
                  <Button type="button" variant="ghost" size="icon" className="h-11 w-11" aria-label={t("editor.item.remove")} disabled={payload.components.length === 1 && !freeLine} onClick={() => {
                    setPayload((current) => ({ ...current, components: current.components.filter((_, i) => i !== index) }));
                    setLineKinds((current) => current.filter((_, i) => i !== index));
                  }}>
                    <Trash2 className="h-4 w-4" aria-hidden="true" />
                  </Button>
                </div>
              );
            })}
            <div className="flex flex-wrap gap-2">
              <Button type="button" variant="outline" className="h-10" onClick={() => {
                setPayload((current) => ({ ...current, components: [...current.components, { medication_id: "", name: "", concentration: "", free_text: "" }] }));
                setLineKinds((current) => [...current, "component"]);
              }}>
                <Plus className="mr-1.5 h-4 w-4" aria-hidden="true" />{t("editor.formula.addComponent")}
              </Button>
              <Button type="button" variant="outline" className="h-10" onClick={() => {
                setPayload((current) => ({ ...current, components: [...current.components, { medication_id: "", name: "", concentration: "", free_text: "" }] }));
                setLineKinds((current) => [...current, "free"]);
              }}>
                <Plus className="mr-1.5 h-4 w-4" aria-hidden="true" />{t("editor.formula.addFreeLine")}
              </Button>
            </div>
          </div>

          <div className={cn("grid gap-3", !mobile && "grid-cols-3")}>
            <div className="space-y-1.5">
              <Label htmlFor="formula-route" className={label}>{t("composer.administrationRoute")}</Label>
              <Autocomplete options={routes.map((r) => ({ label: r, value: r }))} value={payload.route ?? ""} onChange={(v) => patch({ route: v })} placeholder={t("composer.selectOrType")} emptyMessage={t("composer.noSuggestions")} popoverPortalled={false} />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="formula-days" className={label}>{t("manipulated.treatmentDays")}</Label>
              <Input id="formula-days" inputMode="numeric" value={String(payload.treatment_days ?? "")} onChange={(e) => patch({ treatment_days: e.target.value })} className={inputCls} placeholder={t("manipulated.daysPlaceholder")} />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="formula-doses" className={label}>{t("manipulated.totalDoses")}</Label>
              <Input id="formula-doses" inputMode="numeric" value={String(payload.total_doses ?? "")} onChange={(e) => patch({ total_doses: e.target.value })} className={inputCls} placeholder={t("manipulated.dosesPlaceholder")} />
            </div>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="formula-instructions" className={label}>{t("editor.formula.instructions")}</Label>
            <Textarea id="formula-instructions" rows={2} value={payload.instructions ?? ""} onChange={(e) => patch({ instructions: e.target.value })} className={cn("rounded-[10px]", mobile && "text-base")} />
          </div>
        </>
      ) : (
        <div className="space-y-1.5">
          <Label htmlFor="formula-free" className={label}>{t("editor.formula.fullFormula")}</Label>
          <Textarea id="formula-free" rows={mobile ? 7 : 9} value={payload.free_text ?? ""} onChange={(e) => patch({ free_text: e.target.value })} className={cn("rounded-xl border-2 border-primary", mobile && "text-base")} />
          <p className="text-xs text-muted-foreground">{t("editor.formula.fullFormulaHint")}</p>
        </div>
      )}
      {error ? <p className="text-sm text-destructive" role="alert">{error}</p> : null}
    </ExpandedItemShell>
  );
}
